import React, {forwardRef, useRef} from 'react'
import {SxProp, useRefObjectAsForwardedRef} from '../..'
import * as Select from '@radix-ui/react-select'
import {
  SelectProps,
  SelectTriggerProps,
  SelectViewportProps,
  SelectArrowProps,
  SelectValueProps,
  SelectIconProps,
  SelectPortalProps,
  SelectContentProps,
  SelectItemProps,
  SelectItemTextProps,
  SelectItemIndicatorProps,
  SelectScrollUpButtonProps,
  SelectScrollDownButtonProps,
  SelectGroupProps,
  SelectLabelProps,
  SelectSeparatorProps
} from './types'

// --------------------------------------------------------
// Select Root
/**
 *
 * Contains all the parts of a Select menu.
 */
const Root = ({children, ...props}: SelectProps) => {
  return <Select.Root {...props}>{children}</Select.Root>
}
// --------------------------------------------------------
// Select Trigger
/**
 *
 * The button that toggles the Select menu. The Select.Content will position itself by aligning over the trigger.
 *
 * [data-state]: `open` | `closed
 *
 * [data-disabled]: present when disabled
 *
 * [data-placeholder]: present when has placeholder
 *
 */
const Trigger = forwardRef(({children, asChild = false, ...props}: SelectTriggerProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.Trigger ref={innerRef} {...props}>
      {children}
    </Select.Trigger>
  )
})
// --------------------------------------------------------
// Select Value
/**
 *
 * The part that reflects the selected value. By default the selected item's text will be rendered.
 * If you require more control, you can instead control the select and pass your own children.
 * It should not be styled to ensure correct positioning.
 * An optional placeholder prop is also available for when the select has no value.
 *
 */
const Value = forwardRef(({children, asChild = false, ...props}: SelectValueProps, forwardedRef) => {
  const innerRef = useRef<HTMLSpanElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.Value ref={innerRef} {...props}>
      {children}
    </Select.Value>
  )
})

// --------------------------------------------------------
// Select Icon
/**
 *
 * A small icon often displayed next to the value as a visual affordance for the fact it can be open. By default renders ▼ but you can use your own icon via asChild or use children.
 *
 */
const Icon = forwardRef(({children, asChild = false, UNSAFE_className, ...props}: SelectIconProps, forwardedRef) => {
  const innerRef = useRef<HTMLSpanElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.Icon asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
      {children}
    </Select.Icon>
  )
})

// --------------------------------------------------------
// Select Portal
/**
 *
 * When used, portals the content part into the body.
 *
 */
const Portal = ({children, container = document.body, ...props}: SelectPortalProps) => {
  return (
    <Select.Portal container={container} {...props}>
      {children}
    </Select.Portal>
  )
}

// --------------------------------------------------------
// Select Content
/**
 *
 * The component that pops out when the Select menu is open.
 *
 */
const Content = forwardRef(({children, ...props}: SelectContentProps & SxProp, forwardedRef) => {
  const {
    asChild,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    position = 'item-aligned',
    side = 'bottom',
    sideOffset = 0,
    align = 'center',
    alignOffset = 0,
    avoidCollisions = true,
    collisionBoundary = [],
    collisionPadding = 10,
    arrowPadding = 0,
    sticky = 'partial',
    hideWhenDetached = false,
    sx,
    UNSAFE_className
  } = props
  const innerContentRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerContentRef)
  return (
    <Select.Content
      asChild={asChild}
      onCloseAutoFocus={onCloseAutoFocus}
      onEscapeKeyDown={onEscapeKeyDown}
      onPointerDownOutside={onPointerDownOutside}
      position={position}
      side={side}
      sideOffset={sideOffset}
      align={align}
      alignOffset={alignOffset}
      avoidCollisions={avoidCollisions}
      collisionBoundary={collisionBoundary}
      collisionPadding={collisionPadding}
      arrowPadding={arrowPadding}
      sticky={sticky}
      hideWhenDetached
      className={UNSAFE_className}
      sx={sx}
      ref={innerContentRef}
      {...props}
    >
      {children}
    </Select.Content>
  )
})

//
// Select Viewport
/**
 *
 * The scrolling viewport that contains all of the items.
 */
const Viewport = forwardRef(
  ({children, asChild = false, UNSAFE_className, ...props}: SelectViewportProps, forwardedRef) => {
    const innerRef = useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)
    return (
      <Select.Viewport asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
        {children}
      </Select.Viewport>
    )
  }
)

// --------------------------------------------------------
// Select Item
/**
 *
 * The component that contains the select items.
 * [data-state]: 'checked' | 'unchecked'
 * [data-highlighted]: present when highlighted
 * [data-disabled]: present when disabled
 *
 */

const Item = React.forwardRef(({children, ...props}: SelectItemProps, forwardedRef) => {
  const {asChild = false, disabled, textValue, sx, UNSAFE_className} = props
  const innerItemRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerItemRef)
  return (
    <Select.Item
      asChild={asChild}
      disabled={disabled}
      textValue={textValue}
      sx={sx}
      className={UNSAFE_className}
      ref={innerItemRef}
      {...props}
    >
      {children}
    </Select.Item>
  )
})

// --------------------------------------------------------
// Select ItemText
/**
 *
 * The textual part of the item. It should only contain the text you want to see in the trigger when that item is selected. It should not be styled to ensure correct positioning.
 *
 */
const ItemText = forwardRef(({children, ...props}: SelectItemTextProps, forwardedRef) => {
  const {asChild = false} = props
  const innerRef = React.useRef<HTMLSpanElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.ItemText asChild={asChild} ref={innerRef} {...props}>
      {children}
    </Select.ItemText>
  )
})

// --------------------------------------------------------
// Select ItemIndicator
/**
 *
 * Renders when the item is selected. You can style this element directly, or you can use it as a wrapper to put an icon into, or both.
 *
 */
const ItemIndicator = forwardRef(
  ({children, asChild, UNSAFE_className, ...props}: SelectItemIndicatorProps, forwardedRef) => {
    const innerRef = React.useRef<HTMLSpanElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)
    return (
      <Select.ItemIndicator asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
        {children}
      </Select.ItemIndicator>
    )
  }
)

// --------------------------------------------------------
// Select ScrollUpButton
/**
 *
 * An optional button used as an affordance to show the viewport overflow as well as functionally enable scrolling upwards.
 *
 */
const ScrollUpButton = forwardRef(
  ({children, asChild = false, UNSAFE_className, ...props}: SelectScrollUpButtonProps, forwardedRef) => {
    const innerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)
    return (
      <Select.ScrollUpButton asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
        {children}
      </Select.ScrollUpButton>
    )
  }
)

// --------------------------------------------------------
// Select ScrollDownButton
/**
 *
 * An optional button used as an affordance to show the viewport overflow as well as functionally enable scrolling downwards.
 *
 */
const ScrollDownButton = forwardRef(
  ({children, asChild = false, UNSAFE_className, ...props}: SelectScrollDownButtonProps, forwardedRef) => {
    const innerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)
    return (
      <Select.ScrollDownButton asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
        {children}
      </Select.ScrollDownButton>
    )
  }
)

// --------------------------------------------------------
// Select Group
/**
 *
 * Used to group multiple items. use in conjunction with Select.Label to ensure good accessibility via automatic labelling.
 *
 */
const Group = forwardRef(({children, asChild = false, ...props}: SelectGroupProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.Group asChild={asChild} ref={innerRef} {...props}>
      {children}
    </Select.Group>
  )
})

// --------------------------------------------------------
// Select Label
/**
 *
 * Used to render the label of a group. It won't be focusable using arrow keys.
 *
 */
const Label = forwardRef(({children, asChild = false, UNSAFE_className, ...props}: SelectLabelProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.Label asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
      {children}
    </Select.Label>
  )
})

// --------------------------------------------------------

// --------------------------------------------------------
// Select Separator
/**
 *
 * Used to visually separate items in the Select menu.
 *
 */
const Separator = forwardRef(({children, asChild, UNSAFE_className, ...props}: SelectSeparatorProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Select.Separator asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
      {children}
    </Select.Separator>
  )
})

// --------------------------------------------------------
// Select Arrow
/**
 *
 * An optional arrow element to render alongside the content. This can be used to help visually link the trigger with the Select.Content. Must be rendered inside Select.Content. Only available when position is set to popper.
 *
 */
const Arrow = forwardRef(
  ({children, asChild = false, UNSAFE_className, width = 10, height = 5, ...props}: SelectArrowProps, forwardedRef) => {
    const innerRef = React.useRef<SVGSVGElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)
    return (
      <Select.Arrow asChild={asChild} className={UNSAFE_className} ref={innerRef} {...props}>
        {children}
      </Select.Arrow>
    )
  }
)
// --------------------------------------------------------
// Exports
Root.displayName = 'Select'
Trigger.displayName = 'Select.Trigger'
Value.displayName = 'Select.Value'
Icon.displayName = 'Select.Icon'
Portal.displayName = 'Select.Portal'
Content.displayName = 'Select.Content'
Viewport.displayName = 'Select.Viewport'
Item.displayName = 'Select.Item'
ItemText.displayName = 'Select.ItemText'
ItemIndicator.displayName = 'Select.ItemIndicator'
ScrollUpButton.displayName = 'Select.ScrollUpButton'
ScrollDownButton.displayName = 'Select.ScrollDownButton'
Group.displayName = 'Select.Group'
Label.displayName = 'Select.Label'
Separator.displayName = 'Select.Separator'
Arrow.displayName = 'Select.Arrow'
/**
 * Displays a list of options for the user to pick from—triggered by a button.
 */
export default Object.assign(Root, {
  Trigger,
  Value,
  Icon,
  Portal,
  Content,
  Viewport,
  Item,
  ItemText,
  ItemIndicator,
  ScrollUpButton,
  ScrollDownButton,
  Group,
  Label,
  Separator,
  Arrow
})
